
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Box,
} from '@mui/material';
import { PtoRequest } from '../../types/adjustments';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

interface PtoDaysProps {
    open: boolean;
    onCancel: () => void;
    onConfirm: (approvedDays: number, approvedHours: number) => void;
    ptoRequest: PtoRequest | null;
    ptoHoursPerDay: number;
}

const roundPtoValue = (value: number) => {
    return Math.round(value * 100) / 100;
};

const formatPtoValue = (value: number) => {
    const rounded = roundPtoValue(value);
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toString();
};

export const PtoDays = ({
    open,
    onCancel,
    onConfirm,
    ptoRequest,
    ptoHoursPerDay,
}: PtoDaysProps) => {

    const [ptoDays, setPtoDays] = useState<string>("");
    const [ptoHours, setPtoHours] = useState<string>("");

    const startDateValue = ptoRequest?.startDate ?? "";
    const endDateValue = ptoRequest?.endDate ?? "";

    const requestedDays = useMemo(() => {
        const startDate = dayjs(startDateValue);
        const endDate = dayjs(endDateValue);
        if (!startDate.isValid() || !endDate.isValid()) return 0;
        return endDate.diff(startDate, "day") + 1;
    }, [startDateValue, endDateValue]);

    useEffect(() => {
        if (!open) return;

        const defaultDays = requestedDays > 0 ? requestedDays : 0;
        setPtoDays(defaultDays ? formatPtoValue(defaultDays) : "");
        setPtoHours(defaultDays && ptoHoursPerDay > 0
            ? formatPtoValue(defaultDays * ptoHoursPerDay)
            : "");
    }, [open, ptoHoursPerDay, requestedDays]);

    const handleConfirm = () => {
        const days = Number(ptoDays);
        const hours = Number(ptoHours);
        if (!isNaN(days) && days >= 0 && !isNaN(hours) && hours >= 0) {
            onConfirm(days, hours);
        } else {
            // Optionally, show an error or ignore
            console.warn("Invalid PTO days or hours");
        }
    };

    const handleDaysChange = (value: string) => {
        setPtoDays(value);

        if (value === "") {
            setPtoHours("");
            return;
        }

        const days = Number(value);
        if (!isNaN(days) && ptoHoursPerDay > 0) {
            setPtoHours(formatPtoValue(days * ptoHoursPerDay));
        }
    };

    const handleHoursChange = (value: string) => {
        setPtoHours(value);

        if (value === "") {
            setPtoDays("");
            return;
        }

        const hours = Number(value);
        if (!isNaN(hours) && ptoHoursPerDay > 0) {
            setPtoDays(formatPtoValue(hours / ptoHoursPerDay));
        }
    };

    const requestedDates = useMemo(() => {
        const startDate = dayjs(startDateValue);
        const endDate = dayjs(endDateValue);

        if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate, "day")) {
            return [];
        }

        const dates: string[] = [];
        for (let currentDate = startDate; !currentDate.isAfter(endDate, "day"); currentDate = currentDate.add(1, "day")) {
            dates.push(currentDate.format("dddd MM-DD-YYYY"));
        }

        return dates;
    }, [startDateValue, endDateValue]);

    const dateRangeText = useMemo(() => {
        const startDate = dayjs(startDateValue);
        const endDate = dayjs(endDateValue);

        return startDate.isValid() && endDate.isValid()
            ? `${startDate.format("MM-DD-YYYY")} to ${endDate.format("MM-DD-YYYY")}`
            : "";
    }, [startDateValue, endDateValue]);

    return (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>Approve PTO Request</DialogTitle>
            <DialogContent>
                 <Typography>
                    This request covers {requestedDays} days ({dateRangeText}). How much should count toward the employee's PTO total?
                </Typography>
                {ptoHoursPerDay > 0 ? (
                    <Typography sx={{ marginTop: 1 }}>
                        1 day = {ptoHoursPerDay} hours for this employee.
                    </Typography>
                ) : null}
                {requestedDates.length > 0 ? (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography variant="subtitle2">Requested days:</Typography>
                        <Box component="ul" sx={{ marginTop: 0.5, paddingLeft: 3 }}>
                            {requestedDates.map((date) => (
                                <li key={date}>{date}</li>
                            ))}
                        </Box>
                    </Box>
                ) : null}
                <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
                    <TextField
                        label="PTO Days to be used"
                        type="number"
                        value={ptoDays}
                        onChange={(e) => handleDaysChange(e.target.value)}
                        variant="outlined"
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                        label="PTO Hours to be used"
                        type="number"
                        value={ptoHours}
                        onChange={(e) => handleHoursChange(e.target.value)}
                        variant="outlined"
                        fullWidth
                        inputProps={{ min: 0, step: 0.25 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} style={{ color: "black" }}>Cancel</Button>
                <Button onClick={() => handleConfirm()} style={{ backgroundColor: "black" }} variant="contained">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};
